/**
 * MIC-3 — admin approval queue for pending new-item proposals.
 */
import type { MfgInventoryCountProposal } from "@/lib/manufacturing/types/inventoryCount";
import {
  ghostBtnClass,
  mfgPrimaryBtnClass,
  mfgSecondaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";

type Props = {
  proposals: MfgInventoryCountProposal[];
  canReview: boolean;
  busy: boolean;
  reviewingId: string | null;
  onApprove: (proposalId: string) => void;
  onReject: (proposalId: string) => void;
  onRefresh: () => void;
};

export function ManufacturingInventoryCountProposalsPanel({
  proposals,
  canReview,
  busy,
  reviewingId,
  onApprove,
  onReject,
  onRefresh,
}: Props) {
  const pending = proposals.filter((p) => p.status === "pending");
  const reviewed = proposals.filter((p) => p.status !== "pending");

  return (
    <div className="space-y-3 border-t border-gray-200 pt-4 dark:border-zinc-700">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-50">
          New-item proposals
          {pending.length > 0 ? (
            <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-900 dark:bg-amber-900/50 dark:text-amber-100">
              {pending.length} pending
            </span>
          ) : null}
        </h3>
        <button type="button" className={ghostBtnClass} disabled={busy} onClick={onRefresh}>
          Refresh
        </button>
      </div>

      {!canReview ? (
        <p className="text-[11px] text-gray-500 dark:text-zinc-400">
          Pending proposals wait for an org admin or owner to approve (creates the material) or
          reject.
        </p>
      ) : null}

      {pending.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">No pending proposals.</p>
      ) : (
        <ul className="space-y-2">
          {pending.map((p) => (
            <li
              key={p.id}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900/40"
            >
              <div className="font-medium text-gray-900 dark:text-zinc-50">{p.proposed_name}</div>
              <div className="text-xs text-gray-500 dark:text-zinc-400">
                {p.proposed_category} · {p.proposed_unit}
                {p.proposed_qty != null ? ` · qty ${p.proposed_qty}` : ""}
              </div>
              {canReview ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    type="button"
                    className={mfgPrimaryBtnClass}
                    disabled={busy || reviewingId === p.id}
                    onClick={() => onApprove(p.id)}
                  >
                    {reviewingId === p.id ? "…" : "Approve"}
                  </button>
                  <button
                    type="button"
                    className={mfgSecondaryBtnClass}
                    disabled={busy || reviewingId === p.id}
                    onClick={() => onReject(p.id)}
                  >
                    Reject
                  </button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {reviewed.length > 0 ? (
        <details className="text-xs text-gray-500 dark:text-zinc-400">
          <summary className="cursor-pointer">Recently reviewed ({reviewed.length})</summary>
          <ul className="mt-2 space-y-1">
            {reviewed.slice(0, 8).map((p) => (
              <li key={p.id}>
                {p.proposed_name} — {p.status}
                {p.resulting_material_id ? ` → ${p.resulting_material_id.slice(0, 8)}…` : ""}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}
